<template>
  <div class="container">
    <DataGetter
      :url="`${apiRoute}/dashboard`"
      :params="{
        region: selectedRegion,
        product: 'Renewable electricity',
        flow: indicatorFlows,
      }"
      :childElementType="null"
      :initialData="
        indicatorFlows.reduce((flows, flow) => {
          if (flow === 'Forecast revision' || flow === 'VRE phase') {
            flows.push({ flow, value: 0, unit: '%' });
          } else {
            flows.push({ flow, value: 0, year: 2022, unit: '%' });
            flows.push({ flow, value: 0, year: 2028, unit: '%' });
          }
          return flows;
        }, [])
      "
      v-slot="{ data }"
    >
      <IndicatorBox
        :title="`Key electricity indicators, ${selectedRegion}`"
        :indicators="
          data
            .toSorted(
              (a, b) =>
                indicatorFlows.indexOf(a.flow) - indicatorFlows.indexOf(b.flow)
            )
            .map((row) => {
              const invalid = !row.value || row.value.includes('Not');
              return {
                title:
                  row.flow === 'Forecast revision'
                    ? 'Forecast revision from Renewables 2022'
                    : `${row.flow}, ${row.year}`,
                number: invalid
                  ? undefined
                  : parseFloat(parseFloat(row.value).toFixed(0)),
                invalid,
                units: row.unit === '%' ? row.unit : null,
                text: row.flow === 'VRE phase' && !invalid ? 'Phase ' : null,
                description:
                  typeof row.value === 'string' && row.value.includes('Not')
                    ? row.value
                    : row.unit !== '%'
                    ? row.unit
                    : row.year === 2028
                    ? 'Forecast'
                    : null,
                arrow: row.flow === 'Forecast revision',
                info: indicatorNotes[row.flow],
              };
            })
        "
        :notes="countryNotes[selectedRegion]"
        compact
      >
      </IndicatorBox>
    </DataGetter>

    <DataGetter
      :url="`${apiRoute}/dashboard`"
      :params="{
        publication,
        region: selectedRegion,
        flow: 'TARGETQUALIFIER',
      }"
      @fetched="(data) => (targetText = data.length > 0 ? data[0].value : '')"
    />
    <DataGetter
      :url="endpoint"
      :params="{
        region: selectedRegion,
        publication: publication,
      }"
      class="container"
      v-slot="{ data }"
      lazy
    >
      <ControlGroup>
        <ButtonGroup
          label="Show"
          :options="[
            { label: 'Cumulative capacity', value: 'Capacity' },
            { label: 'Net additions', value: 'Net additions' },
            { label: 'Generation', value: 'Generation' },
          ]"
          v-model="selectedFlow"
          fixed
        />
      </ControlGroup>
      <div class="m-block m-block--charts m-block--charts-full">
        <TimeSeriesChart
          :products="
            selectedFlow === 'Generation'
              ? generationProducts
              : electricityProducts
          "
          :selectedFlow="selectedFlow"
          :allowCaseSelection="selectedFlow !== 'Generation'"
          :data="data"
          :targetText="
            selectedFlow === 'Capacity' && targetText ? targetText : null
          "
          :note="
            selectedFlow !== 'Generation'
              ? 'Generation values do not include generation associated with renewable power for hydrogen production. PV data for all countries have been unified to direct current (DC) and may differ from previous editions of this report. Because some countries\' 2030 solar PV ambitions may be based on alternating current, this may not accurately reflect the difference between 2022 total capacity and 2030 ambition capacity. Conversions are based on an IEA survey of more than 80 countries and interviews with PV industry associations.'
              : null
          "
          :is-world="selectedRegion === 'World'"
        />
      </div>
      <div class="m-block m-block--charts m-block--charts-half">
        <ProcurementChart
          :selectedRegion="selectedRegion"
          selectedProduct="Total"
          :apiRoute="apiRoute"
        />
        <FiveYearChart
          title="5-year capacity growth"
          :selectedRegion="selectedRegion"
          selectedFlow="Net additions"
          :products="allProducts"
          :allProducts="electricityProducts"
          productLabel="Generation technology"
          showAll
          :data="data"
          note="PV data for all countries have been unified to direct current (DC) and may differ from previous editions of this report. Conversions are based on an IEA survey of more than 80 countries and interviews with PV industry associations."
        />
      </div>
    </DataGetter>
    <TextContent
      product="Renewable electricity"
      :region="selectedRegion"
      :apiRoute="apiRoute"
      :publication="publication"
    />
  </div>
</template>

<script>
import {
  DataGetter,
  ControlGroup,
  ButtonGroup,
  IndicatorBox,
} from "@international-energy-agency/vue-ui";
import TimeSeriesChart from "./TimeSeriesChart.vue";
import ProcurementChart from "./ProcurementChart.vue";
import FiveYearChart from "./FiveYearChart.vue";
import TextContent from "./TextContent.vue";
export default {
  props: {
    apiRoute: {
      type: String,
      default: "https://api.iea.org/renewables",
    },
    publication: {
      type: String,
      default: "2023",
    },
    selectedRegion: {
      type: String,
      required: true,
    },
    countryNotes: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    DataGetter,
    ControlGroup,
    ButtonGroup,
    IndicatorBox,
    TimeSeriesChart,
    ProcurementChart,
    FiveYearChart,
    TextContent,
  },
  data() {
    return {
      selectedFlow: "Capacity",
      secondChart: "Capacity growth",
      electricityProducts: [
        "Hydro",
        "Ocean",
        "Bioenergy",
        "Geothermal",
        "PV utility-scale systems",
        "PV distributed systems",
        "Concentrating solar power",
        "Offshore wind",
        "Onshore wind",
        "Renewables dedicated to H2 production",
      ],
      generationProducts: [
        "Hydro",
        "Ocean",
        "Bioenergy",
        "Geothermal",
        "Solar PV",
        "",
        "Concentrating solar power",
        "Offshore wind",
        "Onshore wind",
      ],
      additionalProducts: [
        "PV off-grid",
        "PV commercial systems",
        "PV residential systems",
        "Offshore wind dedicated to H2 production",
        "Onshore wind dedicated to H2 production",
        "Solar PV dedicated to H2 production",
      ],
      indicatorFlows: [
        "Share of renewable energy in power generation",
        "Share of VRE in power generation",
        "VRE phase",
        "Forecast revision",
      ],
      indicatorNotes: {
        "VRE phase":
          "The integration of variable renewable energy (VRE), such as wind and solar, can be categorised into a six-phase framework, which can be used to prioritise different measures to support system flexibility, identify relevant challenges and implement appropriate measures to support the system integration of VRE. <strong>Phase 1</strong> captures very early stages where VRE deployment has no immediate impact on power system operation. In <strong>Phase 2</strong> flexibility issues emerge but the system is able to cope with them through minor operational modifications. <strong>Phases 3 through 6</strong> indicate the increasing influence of VRE in determining system operations.",
      },
      targetText: "",
    };
  },
  computed: {
    endpoint() {
      const { apiRoute } = this;
      return `${apiRoute}/market`;
    },
    caseOptions() {
      const { selectedFlow } = this;
      const options = [{ label: "Main case", value: "MAIN" }];
      if (selectedFlow !== "Generation") {
        options.push({ label: "Accelerated case", value: "ACC" });
      }
      return options;
    },
    allProducts() {
      const { electricityProducts, additionalProducts } = this;
      return [...electricityProducts, ...additionalProducts];
    },
  },
};
</script>

<style scoped>
.case-select {
  flex: unset !important;
}
.m-block--charts-full > .m-chart-block {
  margin-top: 0px;
}
</style>